import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, MaskText } from "@/components/motion/Reveal";
import { api, formatApiError } from "@/lib/api";
import { ORG_TYPES, GROUP_PROGRAMMES } from "@/lib/learning";

const inputCls = "w-full bg-transparent border-b border-ptf-primary/25 py-2.5 text-sm outline-none transition-colors duration-300 focus:border-ptf-secondary placeholder:text-ptf-text/40 text-ptf-text";
const Eyebrow = ({ children }) => (
  <p className="text-[0.7rem] uppercase tracking-[0.28em] mb-5 flex items-center gap-3 text-ptf-primary/60">
    <span className="h-px w-8 inline-block bg-ptf-primary/40" />{children}
  </p>
);

function EnquiryForm() {
  const [form, setForm] = useState({ organisation: "", org_type: "", name: "", email: "", phone: "", participants: "", location: "", timeframe: "", message: "" });
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (p) => setInterests((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/group-enquiries", { ...form, programme_interest: interests });
      setDone(data.message);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send the enquiry.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div data-testid="group-enquiry-success" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="py-4">
        <span className="grid place-items-center h-12 w-12 rounded-full bg-ptf-accent text-ptf-primary mb-5"><Check className="h-6 w-6" /></span>
        <h3 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">Received.</h3>
        <p className="mt-3 text-sm text-ptf-text/75 leading-relaxed max-w-md">{done}</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="group-enquiry-form" className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <input required data-testid="ge-organisation" value={form.organisation} onChange={set("organisation")} placeholder="Organisation *" className={inputCls} />
        <select data-testid="ge-org-type" value={form.org_type} onChange={set("org_type")} className={inputCls}>
          <option value="">Type of organisation</option>
          {ORG_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>
        <input required data-testid="ge-name" value={form.name} onChange={set("name")} placeholder="Contact person *" className={inputCls} />
        <input required type="email" data-testid="ge-email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} />
        <input data-testid="ge-phone" value={form.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} />
        <input data-testid="ge-participants" value={form.participants} onChange={set("participants")} placeholder="Approximate participants" className={inputCls} />
        <input data-testid="ge-location" value={form.location} onChange={set("location")} placeholder="Preferred location" className={inputCls} />
        <input data-testid="ge-timeframe" value={form.timeframe} onChange={set("timeframe")} placeholder="Timeframe" className={inputCls} />
      </div>
      <div>
        <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/55 mb-3">Programme interests</p>
        <div className="flex flex-wrap gap-2">
          {GROUP_PROGRAMMES.map((p) => (
            <button
              key={p}
              type="button"
              data-testid={`ge-interest-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => toggle(p)}
              className={`text-[0.65rem] uppercase tracking-[0.12em] px-3 py-1.5 border transition-colors ${interests.includes(p) ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/25 text-ptf-text/70 hover:border-ptf-primary"}`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
      <textarea data-testid="ge-message" rows={3} value={form.message} onChange={set("message")} placeholder="What should this programme achieve for your team?" className={`${inputCls} resize-none`} />
      <button type="submit" data-testid="ge-submit" disabled={loading} className="bg-ptf-primary text-ptf-bg px-10 py-3.5 text-[0.72rem] font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors disabled:opacity-60 inline-flex items-center gap-2">
        {loading && <Loader2 className="h-4 w-4 animate-spin" />} Request a Custom Programme
      </button>
    </form>
  );
}

export default function Institutional() {
  return (
    <>
      <Seo title="Train Your Team: Institutional & Group Training" path="/learning/institutional" description="Custom Planet Trotter programmes for schools, colleges, corporates, CSR teams, NGOs, clubs and institutions: leadership, team building, first aid, outdoor skills, environmental education and more." />
      <PageHero
        eyebrow="Field Learning · Institutional"
        titleLines={["Train your", "team."]}
        intro="Custom / group programmes designed around each team: schools, colleges, universities, corporates, CSR teams, NGOs, adventure clubs, sports organisations, research institutions, government bodies and community organisations."
        image="/dawn/vor-raft-group.jpg"
      />
      <section className="bg-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow>How group programmes work</Eyebrow></Reveal>
            <MaskText
              lines={["Designed per team,", "delivered in", "the field."]}
              className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[1.02] tracking-tight text-ptf-primary pb-1"
            />
            <Reveal delay={0.15}>
              <p className="mt-6 text-ptf-text/75 leading-relaxed">
                No two groups get the same programme. We start from your objectives,
                shape the content from the catalogue, and deliver it in classrooms,
                camps or the field, including the signature Leadership Through
                Adventure experience.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-ptf-text/70">
                {["A scoping conversation to understand the team and its objectives", "A proposed programme, location and safety plan", "Delivery by Planet Trotter facilitators", "A structured debrief and, where applicable, Planet Trotter certificates"].map((s, i) => (
                  <li key={s} className="flex gap-3"><span className="font-display text-ptf-primary/30">{String(i + 1).padStart(2, "0")}</span>{s}</li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-7 bg-white border border-ptf-primary/12 p-7 md:p-10">
            <EnquiryForm />
          </div>
        </div>
      </section>
    </>
  );
}
